import React, { useEffect, useRef, useState } from 'react';
import { buildIndicesChartBundlePayload } from '@utils/indicesChartBundle';
import type { IndexInstrumentViewModel, IndicesPeriod } from '@app-types/indices';
import IndicesChartFrame from './IndicesChartFrame';

interface IndicesChartPreviewProps {
  instrument: IndexInstrumentViewModel;
  period: IndicesPeriod;
}

const CHART_STATE_TEXT = {
  loading: '読み込み中…',
  'no-data': 'データなし',
  error: 'データなし',
} as const;

const IndicesChartPreview: React.FC<IndicesChartPreviewProps> = ({ instrument, period }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || instrument.chartState !== 'ready') return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [instrument.chartState]);

  const canRenderChart = instrument.chartState === 'ready'
    && instrument.chartData !== null
    && isNearViewport;

  return (
    <div
      ref={containerRef}
      className="indices-chart-preview"
      aria-hidden="true"
      data-chart-instrument={instrument.id}
      data-chart-period={period}
      data-chart-state={instrument.chartState}
    >
      {canRenderChart ? (
        <IndicesChartFrame
          payload={buildIndicesChartBundlePayload({
            ric: instrument.ric,
            chartData: instrument.chartData as string,
            decimalPlaces: instrument.decimalPlaces,
          }, period, 'card')}
          title={`${instrument.name} チャート`}
        />
      ) : (
        <div className="indices-chart-preview__placeholder">
          {instrument.chartState !== 'ready' && CHART_STATE_TEXT[instrument.chartState]}
        </div>
      )}
    </div>
  );
};

export default IndicesChartPreview;
